// src/components/portal/subscription.jsx
import React, { useState, useEffect, useCallback } from "react";
import "./subscription.css";
import { useAuth } from "../../context/AuthContext";
import { API_BASE_URL } from "../../config/apiConfig";

const Subscription = () => {
  const { user } = useAuth();
  
  // State for token balance
  const [tokenBalance, setTokenBalance] = useState(0);
  const [loadingBalance, setLoadingBalance] = useState(true);
  
  // State for transactions
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(true);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const transactionsPerPage = 10;

  // Fetch token balance
  const fetchTokenBalance = useCallback(async () => {
    if (!user?.username) return;
    try {
      setLoadingBalance(true);
      const res = await fetch(
        `${API_BASE_URL}/tokens/balance?username=${encodeURIComponent(user.username)}`
      );
      const data = await res.json();
      if (data.status === "success") {
        setTokenBalance(data.data?.balance || 0);
      }
    } catch (err) {
      console.error("Failed to fetch token balance:", err);
    } finally {
      setLoadingBalance(false);
    }
  }, [user?.username]);

  // Fetch transaction history
  const fetchTransactions = useCallback(async (page = 1) => {
    if (!user?.username) return;
    try {
      setLoadingTransactions(true);
      const res = await fetch(
        `${API_BASE_URL}/tokens/transactions?username=${encodeURIComponent(user.username)}&page=${page}&limit=${transactionsPerPage}`
      );
      const data = await res.json();
      if (data.status === "success") {
        setTransactions(data.data?.transactions || []);
        setTotalPages(data.data?.total_pages || 1);
        setCurrentPage(page);
      }
    } catch (err) {
      console.error("Failed to fetch transactions:", err);
    } finally {
      setLoadingTransactions(false);
    }
  }, [user?.username]);

  // Initial fetch
  useEffect(() => {
    fetchTokenBalance();
    fetchTransactions(1);
  }, [fetchTokenBalance, fetchTransactions]);

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Format token amount with sign
  const formatTokenAmount = (amount, type) => {
    const num = Math.abs(amount);
    if (type === "credit" || type === "bonus" || type === "purchase" || type === "refund" || amount > 0) {
      return <span className="token-credit">+{num}</span>;
    } else {
      return <span className="token-debit">-{num}</span>;
    }
  };

  // Get transaction type badge
  const getTypeBadge = (type) => {
    const typeClasses = {
      credit: "badge-credit",
      debit: "badge-debit",
      purchase: "badge-purchase",
      bonus: "badge-bonus",
      refund: "badge-refund",
      usage: "badge-usage"
    };
    return (
      <span className={`transaction-badge ${typeClasses[type] || "badge-default"}`}>
        {type.charAt(0).toUpperCase() + type.slice(1)}
      </span>
    );
  };

  // Handle Add More Tokens click
  const handleAddTokens = () => {
    // Placeholder - will redirect to payment page later
    alert("Payment integration coming soon!");
  };

  // Pagination handlers
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      fetchTransactions(page);
    }
  };

  return (
    <>
      <div className="right-top">
        <div className="right-top-title">Subscription</div>
      </div>

      <div className="right-middle subscription-container">
        {/* TOP ROW: Token Balance & Stats */}
        <div className="subscription-top-row">
          {/* Token Balance Card */}
          <div className="subscription-card token-balance-card">
            <div className="card-header">
              <span>Token Balance</span>
            </div>
            <div className="card-body">
              {loadingBalance ? (
                <div className="loading-text">Loading...</div>
              ) : (
                <div className="token-balance-content">
                  <div className="token-balance-display">
                    <div className="token-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M12 6v12M8 10h8M8 14h8"/>
                      </svg>
                    </div>
                    <div className="token-amount">
                      <span className="token-number">{tokenBalance.toLocaleString()}</span>
                      <span className="token-label">Tokens Available</span>
                    </div>
                  </div>
                  
                  <div className="token-info">
                    <p className="token-rate">1 Token ≈ $0.01 USD</p>
                    <p className="token-value">Balance Value: ${(tokenBalance * 0.01).toFixed(2)} USD</p>
                  </div>

                  <button 
                    className="add-tokens-btn"
                    onClick={handleAddTokens}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M12 8v8M8 12h8"/>
                    </svg>
                    Add More Tokens
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Usage Stats Card */}
          <div className="subscription-card usage-stats-card">
            <div className="card-header">
              <span>Usage Summary</span>
            </div>
            <div className="card-body">
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-value">
                    {transactions.filter(t => t.transaction_type === "usage").length}
                  </span>
                  <span className="stat-label">Transactions This Month</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">
                    {transactions
                      .filter(t => t.transaction_type === "usage" || t.amount < 0)
                      .reduce((sum, t) => sum + Math.abs(t.amount), 0)
                      .toLocaleString()}
                  </span>
                  <span className="stat-label">Tokens Used This Month</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* BOTTOM ROW: Transaction History */}
        <div className="subscription-bottom-row">
          <div className="subscription-card transaction-card">
            <div className="card-header">
              <span>Transaction History</span>
              <span className="transaction-count">
                Page {currentPage} of {totalPages}
              </span>
            </div>
            <div className="card-body">
              {loadingTransactions ? (
                <div className="loading-text">Loading transactions...</div>
              ) : transactions.length === 0 ? (
                <div className="empty-state">
                  <p>No transactions yet</p>
                  <p className="empty-hint">Your token usage history will appear here</p>
                </div>
              ) : (
                <>
                  <div className="table-wrapper">
                    <table className="transaction-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Date & Time</th>
                          <th>Type</th>
                          <th>Description</th>
                          <th>Amount</th>
                          <th>Balance After</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((txn, idx) => (
                          <tr key={txn.id || idx}>
                            <td>{(currentPage - 1) * transactionsPerPage + idx + 1}</td>
                            <td className="txn-date">{formatDate(txn.created_at)}</td>
                            <td>{getTypeBadge(txn.transaction_type)}</td>
                            <td className="txn-description">{txn.description || "—"}</td>
                            <td className="txn-amount">
                              {formatTokenAmount(txn.amount, txn.transaction_type)}
                            </td>
                            <td className="txn-balance">{txn.balance_after?.toLocaleString() || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="pagination">
                      <button 
                        className="pagination-btn"
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        ← Previous
                      </button>
                      <span className="pagination-info">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button 
                        className="pagination-btn"
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="right-bottom-bar"></div>
    </>
  );
};

export default Subscription;
